import { Component, inject, OnInit, Output, EventEmitter } from "@angular/core";
import { MaterialModule } from "../../../shared";
import { ColumnConfig, ColumnType, EllipsisDirection } from "../../../shared/models/material-common-models";
import { DynamicTableComponent } from "../../../shared/dynamic-table/dynamic-table.component";
import { AppSettingsService, SpinnerService } from "../../../core";
import { MessageService } from "../../../main";
import { catchError, finalize, throwError, map } from "rxjs";
import { HttpErrorResponse } from "@angular/common/http";
import { BucketItemService } from "../service/bucket-item.service";
import { ITemplates } from "../models/templates.model";
import { MatDialog } from "@angular/material/dialog";
import { ViewTemplateComponent } from "./view-template/view-template.component";
import { CreateFolderComponent } from "../create-folder/create-folder.component";
import { ErrorConstants, LoadTemplateModeConstants, TemplateDialogTitleConstants } from "../constants/component-constants";
import { ActivatedRoute } from "@angular/router";
import { IBucketItemRequest } from "../models/bucket-item-request.model";

@Component({
    selector: 'app-template',
    imports: [MaterialModule, DynamicTableComponent],
    templateUrl: './template.component.html',
    styleUrls: ['./template.component.scss'],
    providers: [BucketItemService]
})
export class TemplateComponent implements OnInit{

  private bucketItemService = inject(BucketItemService);
  private spinnerService = inject(SpinnerService);
  private messageService = inject(MessageService);
  private appSettingService = inject(AppSettingsService);
  private dialog = inject(MatDialog);
  private activatedRoute = inject(ActivatedRoute);
  
  @Output() getAllBucketItemsRequest = new EventEmitter<IBucketItemRequest>();
  
  tableItems:any[] = [];
  columnList: ColumnConfig[] = [];
  templates:ITemplates[] = [];
  templatesCount = 0;
  isSubfolderLevel = false;

  ngOnInit(): void {
    const params = this.activatedRoute.snapshot.queryParams;
    this.isSubfolderLevel = !!params['packageId'];
    
    this.columnList = [
      { columnName: 'FolderTemplate', isClickable:true, ellipsisDirection:EllipsisDirection.None, columnType:ColumnType.NullHeader, isSortable:false, isPuid:false  },
      { columnName: 'Name', isClickable: true , ellipsisDirection:EllipsisDirection.Back, columnType:ColumnType.General, isSortable:true, isPuid:false},
      { columnName: 'ViewTemplate', isClickable:true, ellipsisDirection:EllipsisDirection.None, columnType:ColumnType.NullHeader, isSortable:false, isPuid:false  },
      { columnName: 'LoadTemplate', isClickable:true, ellipsisDirection:EllipsisDirection.None, columnType:ColumnType.NullHeader, isSortable:false, isPuid:false  },
      { columnName: 'DownloadTemplate', isClickable:true, ellipsisDirection:EllipsisDirection.None, columnType:ColumnType.NullHeader, isSortable:false, isPuid:false  }
    ];
    this.columnList = [...this.columnList];
    this.getMaliciousFiles();
  }

  private getMaliciousFiles(){
    this.spinnerService.show(true);
    this.bucketItemService
      .GetTemplates(this.isSubfolderLevel ? 'Subfolder' : 'Package')
      .pipe(
        map((response: ITemplates[]) => {
          this.templates = [...response];
            this.tableItems = response.map(item => ({
              FolderTemplate: `<img class="icon-layout  m-t-xs" src="assets/images/bucket-icons/icon-folder.svg" alt="Folder" />$`,
              Name: item.TemplateName,
              ViewTemplate: `<div class="icon-ic-view"></div>$View`,
              LoadTemplate: `<div class="icon-ic-add"></div>$Load`,
              DownloadTemplate: `<div class="icon-ic-download"></div>$Download`,
            }));
            this.templatesCount = response.length;
          }),
        catchError((error: HttpErrorResponse) => {
          if(error.error.detail !== undefined){
              this.messageService.alert(
                error.error.detail,
                this.appSettingService.toastrDisplayTime
                );
              this.tableItems =[];
              this.templates = [];
              this.templatesCount = 0;
          }
          else{
            this.messageService.alert(
              'Templates cannot be fetched at the moment. Please try again later.',
              this.appSettingService.toastrDisplayTime
            );
          }
          return throwError(() => new Error(error.message));     
        }),
        finalize(()=>{
          this.spinnerService.show(false);
        })
      )
      .subscribe();
  }
  
  clickedData(clickedData:{event:Event, columnName: string, rowData: any}) {
      if(clickedData.columnName === 'DownloadTemplate'){
        this.bucketItemService.downloadTemplate(this.templates[clickedData.rowData.rowId].TemplateId)
          .pipe(
            catchError((error: HttpErrorResponse) => {
              this.messageService.alert(
                ErrorConstants.TEMPLATE_DOWNLOAD_FAILED,
                this.appSettingService.toastrDisplayTime
              );
              return throwError(() => new Error(error.message));
            })
          )
          .subscribe((res: any) => {
            if (res && res.body) {
              const blob = new Blob([res.body], { type: 'application/octet-stream' });
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              const now = new Date();
              const day = String(now.getDate()).padStart(2, '0');
              const month = String(now.getMonth() + 1).padStart(2, '0');
              const year = now.getFullYear();
              const hours = String(now.getHours()).padStart(2, '0');
              const seconds = String(now.getSeconds()).padStart(2, '0');
              const timestamp = `${day}-${month}-${year}-${hours}-${seconds}`;
              let filename = this.templates[clickedData.rowData.rowId].TemplateName + '_' + timestamp + '.zip';
              link.download = filename;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
            }
          });
    }
    else if(clickedData.columnName === 'ViewTemplate' || clickedData.columnName === 'Name'){
      this.dialog.open(ViewTemplateComponent,{
        width:"860px",
        disableClose:false,
        height:"560px",
        position: {bottom: '20px', left: '160px'},
        data: this.templates[clickedData.rowData.rowId].TemplateStructure
      });
    }
    else if(clickedData.columnName === 'LoadTemplate'){
      const currentTemplate = this.templates[clickedData.rowData.rowId];
      const params = this.activatedRoute.snapshot.queryParams;

      if (!navigator.onLine) {
        this.messageService.alert(
          'You are offline. Cannot load template at this time.',
          this.appSettingService.toastrDisplayTime
        );
        return;
      }

      const isSubFolder = !!params['packageId'];
      const isStudy = params['bucketType'] === 'StudyDataCollection';

      const dialogRef = this.dialog.open(CreateFolderComponent, {
        width: '500px',
        disableClose: false,
        data: {
          isFromLoadTemplate: true,
          templateId: currentTemplate.TemplateId,
          templateName: currentTemplate.TemplateName,
          bucketId: params['bucketId'],
          directoryName: params['directoryName'],
          bucketType: params['bucketType'],
          buCode: params['buCode'],
          parentPackageId: params['packageId'],
          mode: isStudy && !isSubFolder
            ? LoadTemplateModeConstants.STUDY
            : isSubFolder
              ? LoadTemplateModeConstants.SUBFOLDER
              : LoadTemplateModeConstants.PACKAGE,
          dialogTitle: isStudy
            ? TemplateDialogTitleConstants.STUDY
            : isSubFolder
              ? TemplateDialogTitleConstants.SUBFOLDER
              : TemplateDialogTitleConstants.PACKAGE
        }
      });

      dialogRef.componentInstance.getAllBucketItemsRequest
        .subscribe((bucketItemRequest: IBucketItemRequest) => {
          this.getAllBucketItemsRequest.emit(bucketItemRequest);
        });
    }
  }
}
